import forumApi from './forumApi';
import { searchQuery, queryKey } from '@/features/forum-next/models/forumQuery';
import { forumId } from '@/features/forum-next/models/forumMappers';
import { ForumError, forumEnvelope, forumError, invalidResponse, staleResponse } from '@/features/forum-next/api/forumErrors';
import { createForumCache } from '@/features/forum-next/services/forumCache';

export function legacySearchQuery(input = {}) {
  return { ...searchQuery(input), limit: 10 };
}

export function legacySearchUrl(input) {
  const value = legacySearchQuery(input);
  return { q: value.q, exact: String(value.exact), topics: String(value.topics), comments: String(value.comments),
    ...(value.section_id ? { section_id: value.section_id } : {}), page: String(value.page) };
}

function page(payload, input) {
  const count = value => (typeof value === 'number' || typeof value === 'string') && /^(0|[1-9]\d*)$/.test(String(value)) && Number.isSafeInteger(Number(value));
  if (!payload || !Array.isArray(payload.topics) || !Array.isArray(payload.comments)
    || !count(payload.totalTopics) || !count(payload.totalComments)
    || payload.topics.length + payload.comments.length > 10
    || payload.topics.some(item => !item || !forumId(item.nid) || typeof item.title !== 'string')
    || payload.comments.some(item => !item || !forumId(item.nid) || !forumId(item.cid))) throw invalidResponse();
  const totalTopics = Number(payload.totalTopics), totalComments = Number(payload.totalComments);
  if (payload.topics.length > totalTopics || payload.comments.length > totalComments) throw invalidResponse();
  if ((!input.topics && (totalTopics || payload.topics.length)) || (!input.comments && (totalComments || payload.comments.length))) throw invalidResponse();
  return { topics: payload.topics, comments: payload.comments, totalTopics, totalComments,
    pages: Math.max(1, Math.ceil(totalTopics / 10) + Math.ceil(totalComments / 10)) };
}

export function createLegacyForumSearchRepository({ getIdentity, http = forumApi, multipart = false }) {
  const cache = createForumCache(), sectionsCache = createForumCache({ maxEntries: 1, ttl: 300000 });
  let identityKey = null;
  function identity() {
    const value = getIdentity(), key = JSON.stringify(value);
    if (key !== identityKey) { cache.invalidate(); sectionsCache.invalidate(); identityKey = key; }
    if (!forumId(value?.uid)) throw new ForumError('authentication_required', 'Inicia sesión para buscar en el foro.', 401);
    return { key, uid: String(value.uid) };
  }
  function current(expected) { if (identity().key !== expected.key) throw staleResponse(); }
  async function read(cache, key, loader, refresh) {
    const owner = identity();
    return cache.read(owner.key + key, async () => {
      try { const data = await loader(owner); current(owner); return data; }
      catch (error) { current(owner); throw forumError(error); }
    }, { refresh });
  }
  async function sections(refresh = false) {
      return read(sectionsCache, 'sections', async owner => {
        const envelope = forumEnvelope((await http.get('/forum/api/v2/bootstrap', { timeout: 20000 })).data);
        if (String(envelope.meta.uid) !== owner.uid) throw staleResponse();
        if (!Array.isArray(envelope.data.sections)) throw invalidResponse();
        return envelope.data.sections.map(section => {
          if (!forumId(section.id) || typeof section.title !== 'string') throw invalidResponse();
          return { tid: String(section.id), name: section.title };
        });
      }, refresh);
  }
  return {
    sections,
    async search(query, refresh = false) {
      const input = legacySearchQuery(query);
      return read(cache, queryKey('legacy-search', input), async owner => {
        if (!input.q) return { topics: [], comments: [], totalTopics: 0, totalComments: 0, pages: 1 };
        // The historical response has no UID. Establish the authorized session
        // first; a failed bootstrap must not race with and expose search results.
        await sections(); current(owner);
        const body = multipart ? new FormData() : new URLSearchParams();
        const fields = { keyword: input.q, id: input.section_id || '', exact: input.exact, topics: input.topics, comments: input.comments, page: input.page - 1 };
        Object.entries(fields).forEach(([key, value]) => body.append(key, String(value)));
        return page((await http.post('/forum/api/search', body, { timeout: 20000, headers: { 'Content-Type': multipart ? 'multipart/form-data' : 'application/x-www-form-urlencoded' } })).data, input);
      }, refresh);
    },
    reset() { cache.invalidate(); sectionsCache.invalidate(); identityKey = null; }
  };
}
